import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import {
  createTemplate,
  getTemplatesQueryKey,
  listTemplates,
  updateTemplate,
  type CreateTemplateInput,
  type ListTemplatesFilters,
  type TemplateItem,
  type UpdateTemplateInput,
} from '../api/templates.client.js';

export function useTemplates(filters: ListTemplatesFilters = {}) {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  return useQuery({
    queryKey: getTemplatesQueryKey(workspaceId, filters),
    queryFn: () => listTemplates(filters),
    enabled: scope !== null,
  });
}

export function useUpdateTemplateMutation() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();

  return useMutation<TemplateItem, Error, { templateId: string; input: UpdateTemplateInput }>({
    mutationFn: ({ templateId, input }) => updateTemplate(templateId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates', workspaceId] });
    },
  });
}

export function useCreateTemplateMutation() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();

  return useMutation<TemplateItem, Error, CreateTemplateInput>({
    mutationFn: (input) => createTemplate(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates', workspaceId] });
    },
  });
}
